/**
 * Groupes de taxes selon la DGI Burkina Faso
 * Référence: Spécifications Techniques SECeF - Section 7.6
 *
 * Les groupes A-P définissent les différents régimes fiscaux applicables
 * aux opérations de vente. Chaque groupe a un taux de TVA spécifique.
 *
 * IMPORTANT: La TSB (Taxe Spéciale sur les Boissons) n'est PAS incluse
 * dans les groupes de taxes. La TSB est calculée séparément en fonction
 * du type d'article (boissons).
 */
import { InvoiceError } from "../errors/index.js";
/** Taux de TVA standard au Burkina Faso (18%) */
export const TVA_RATE = 0.18;
/** Taux de TVA réduit (10%) */
export const TVA_RATE_REDUCED = 0.10;
/**
 * Les 16 groupes de taxes (A-P) selon la DGI
 *
 * Section 7.6 des Spécifications Techniques:
 *
 * | Groupe | Libellé                    | Description                      | Taux TVA |
 * |--------|----------------------------|----------------------------------|----------|
 * | A      | Exonéré                    | Exempt                           | 0%       |
 * | B      | TVA taxable 1              | TVA au taux normal               | 18%      |
 * | C      | TVA taxable 2              | TVA au taux réduit               | 10%      |
 * | D      | Exportation                | Produits taxables à l'export     | 0%       |
 * | E      | Régime dérogatoire         | Régime spécial                   | 0%       |
 * | F      | Régime dérogatoire         | Régime spécial                   | 18%      |
 * | G      | Régime dérogatoire         | Régime spécial                   | 10%      |
 * | H      | Régime synthétique         | Régime synthétique               | 0%       |
 * | I      | Consignation emballage     | Dépôt sur emballages             | 0%       |
 * | J      | Dépôts, garantie, caution  | Dépôts et garanties              | 0%       |
 * | K      | Débours                    | Remboursements                   | 0%       |
 * | L      | TDT                        | Taxe de développement touristique| 10%      |
 * | M      | Taxe séjour hôtelier       | Taxe de séjour                   | 10%      |
 * | N      | PBA                        | Droits fixes                     | 0%       |
 * | O      | Réservé                    | Réservé pour usage futur         | 0%       |
 * | P      | Réservé                    | Réservé pour usage futur         | 0%       |
 */
export const TAX_GROUPS = {
    /** Groupe A - Exonéré (0% TVA) */
    A: {
        code: "A",
        label: "Exonéré",
        tvaRate: 0,
        isExempt: true,
        isExport: false,
        isSpecialRegime: false,
        isReserved: false,
        description: "Opérations exonérées de TVA (produits de première nécessité, santé, éducation, etc.)",
    },
    /** Groupe B - TVA taxable 1 (18% TVA) */
    B: {
        code: "B",
        label: "TVA taxable 1",
        tvaRate: TVA_RATE,
        isExempt: false,
        isExport: false,
        isSpecialRegime: false,
        isReserved: false,
        description: "Opérations soumises à la TVA au taux normal de 18%",
    },
    /** Groupe C - TVA taxable 2 (10% TVA) */
    C: {
        code: "C",
        label: "TVA taxable 2",
        tvaRate: TVA_RATE_REDUCED,
        isExempt: false,
        isExport: false,
        isSpecialRegime: false,
        isReserved: false,
        description: "Opérations soumises à la TVA au taux réduit de 10%",
    },
    /** Groupe D - Exportation (0% TVA) */
    D: {
        code: "D",
        label: "Exportation",
        tvaRate: 0,
        isExempt: false,
        isExport: true,
        isSpecialRegime: false,
        isReserved: false,
        description: "Opérations d'exportation de produits taxables (TVA à 0%)",
    },
    /** Groupe E - Régime dérogatoire (0% TVA) */
    E: {
        code: "E",
        label: "Régime dérogatoire",
        tvaRate: 0,
        isExempt: false,
        isExport: false,
        isSpecialRegime: true,
        isReserved: false,
        description: "Opérations sous régime dérogatoire avec exonération de TVA",
    },
    /** Groupe F - Régime dérogatoire (18% TVA) */
    F: {
        code: "F",
        label: "Régime dérogatoire",
        tvaRate: TVA_RATE,
        isExempt: false,
        isExport: false,
        isSpecialRegime: true,
        isReserved: false,
        description: "Opérations sous régime dérogatoire avec TVA au taux normal de 18%",
    },
    /** Groupe G - Régime dérogatoire (10% TVA) */
    G: {
        code: "G",
        label: "Régime dérogatoire",
        tvaRate: TVA_RATE_REDUCED,
        isExempt: false,
        isExport: false,
        isSpecialRegime: true,
        isReserved: false,
        description: "Opérations sous régime dérogatoire avec TVA au taux réduit de 10%",
    },
    /** Groupe H - Régime synthétique (0% TVA) */
    H: {
        code: "H",
        label: "Régime synthétique",
        tvaRate: 0,
        isExempt: false,
        isExport: false,
        isSpecialRegime: true,
        isReserved: false,
        description: "Opérations sous régime synthétique d'imposition (RSI)",
    },
    /** Groupe I - Consignation emballage (0% TVA) */
    I: {
        code: "I",
        label: "Consignation emballage",
        tvaRate: 0,
        isExempt: true,
        isExport: false,
        isSpecialRegime: false,
        isReserved: false,
        description: "Montants de consignation pour emballages récupérables",
    },
    /** Groupe J - Dépôts, garantie, caution (0% TVA) */
    J: {
        code: "J",
        label: "Dépôts, garantie, caution",
        tvaRate: 0,
        isExempt: true,
        isExport: false,
        isSpecialRegime: false,
        isReserved: false,
        description: "Dépôts de garantie, cautions et autres sommes remboursables",
    },
    /** Groupe K - Débours (0% TVA) */
    K: {
        code: "K",
        label: "Débours",
        tvaRate: 0,
        isExempt: true,
        isExport: false,
        isSpecialRegime: false,
        isReserved: false,
        description: "Remboursements de frais engagés pour le compte du client",
    },
    /** Groupe L - TDT (10%) */
    L: {
        code: "L",
        label: "TDT",
        tvaRate: TVA_RATE_REDUCED,
        isExempt: false,
        isExport: false,
        isSpecialRegime: false,
        isReserved: false,
        specialTaxType: "TDT",
        description: "Taxe de développement touristique (10%)",
    },
    /** Groupe M - Taxe séjour hôtelier (10%) */
    M: {
        code: "M",
        label: "Taxe séjour hôtelier",
        tvaRate: TVA_RATE_REDUCED,
        isExempt: false,
        isExport: false,
        isSpecialRegime: false,
        isReserved: false,
        specialTaxType: "TAXE_SEJOUR",
        description: "Taxe de séjour dans les établissements hôteliers (10%)",
    },
    /** Groupe N - PBA (0% TVA) */
    N: {
        code: "N",
        label: "PBA",
        tvaRate: 0,
        isExempt: false,
        isExport: false,
        isSpecialRegime: false,
        isReserved: false,
        specialTaxType: "PBA",
        description: "Prélèvement sur les billets d'avion (droits fixes)",
    },
    /** Groupe O - Réservé (0% TVA) */
    O: {
        code: "O",
        label: "Réservé",
        tvaRate: 0,
        isExempt: false,
        isExport: false,
        isSpecialRegime: false,
        isReserved: true,
        description: "Groupe réservé pour usage futur par la DGI",
    },
    /** Groupe P - Réservé (0% TVA) */
    P: {
        code: "P",
        label: "Réservé",
        tvaRate: 0,
        isExempt: false,
        isExport: false,
        isSpecialRegime: false,
        isReserved: true,
        description: "Groupe réservé pour usage futur par la DGI",
    },
};
/** All tax group codes (A-P), for z.enum() and other exhaustive checks. */
export const TAX_GROUP_CODES = Object.keys(TAX_GROUPS);
/** Taux PSVB droit commun - 2% */
export const PSVB_RATE_A = 0.02;
/** Taux PSVB derogation - 1% */
export const PSVB_RATE_B = 0.01;
/** Taux PSVB derogation - 0.2% */
export const PSVB_RATE_C = 0.002;
/** Taux PSVB reserve - 0% */
export const PSVB_RATE_D = 0;
/**
 * Groupes PSVB conformes aux specifications DGI section 7.7
 */
export const PSVB_GROUPS = {
    /** Groupe A - PSVB droit commun (2%) */
    A: {
        code: "A",
        label: "PSVB droit commun",
        rate: PSVB_RATE_A,
        description: "Prélèvement à la source sur vente de biens - taux de droit commun",
    },
    /** Groupe B - PSVB derogation (1%) */
    B: {
        code: "B",
        label: "PSVB dérogation",
        rate: PSVB_RATE_B,
        description: "Prélèvement à la source sur vente de biens - taux dérogatoire 1%",
    },
    /** Groupe C - PSVB derogation (0.2%) */
    C: {
        code: "C",
        label: "PSVB dérogation",
        rate: PSVB_RATE_C,
        description: "Prélèvement à la source sur vente de biens - taux dérogatoire 0.2%",
    },
    /** Groupe D - PSVB reserve (0%) */
    D: {
        code: "D",
        label: "PSVB réservé",
        rate: PSVB_RATE_D,
        description: "Prélèvement à la source sur vente de biens - réservé (exonéré)",
    },
};
/** All PSVB group codes (A-D), for z.enum() and other exhaustive checks. */
export const PSVB_GROUP_CODES = Object.keys(PSVB_GROUPS);
/** Type guard for a PSVB group code, replacing repeated .includes() checks. */
export function isPSVBGroupCode(code) {
    return typeof code === "string" && Object.hasOwn(PSVB_GROUPS, code);
}
/** PSVB rate of a line's group, 0 when it carries none. */
export function psvbRateOf(psvbGroup) {
    return psvbGroup && isPSVBGroupCode(psvbGroup) ? PSVB_GROUPS[psvbGroup].rate : 0;
}
/**
 * Obtenir un groupe de taxe par son code
 */
export function getTaxGroup(code) {
    return TAX_GROUPS[code];
}
/**
 * Obtenir tous les groupes d'exportation
 */
export function getExportGroups() {
    return Object.values(TAX_GROUPS).filter((g) => g.isExport);
}
/**
 * Obtenir tous les groupes exonérés
 */
export function getExemptGroups() {
    return Object.values(TAX_GROUPS).filter((g) => g.isExempt);
}
/**
 * Obtenir tous les groupes de régime spécial (dérogatoire ou synthétique)
 */
export function getSpecialRegimeGroups() {
    return Object.values(TAX_GROUPS).filter((g) => g.isSpecialRegime);
}
/**
 * Obtenir tous les groupes réservés
 */
export function getReservedGroups() {
    return Object.values(TAX_GROUPS).filter((g) => g.isReserved);
}
/**
 * The DGI group of `code`; refuses any code outside A–P
 * (GROUPE_TAXE_INCONNU), so a code of another tax scheme is never read as
 * a group at 0 %.
 */
export function bfTaxGroup(code) {
    if (!Object.prototype.hasOwnProperty.call(TAX_GROUPS, code)) {
        throw new InvoiceError(`Groupe de taxe inconnu : « ${String(code)} » (A à P)`, "GROUPE_TAXE_INCONNU", 400);
    }
    return TAX_GROUPS[code];
}
/**
 * Obtenir le taux de TVA pour un groupe de taxe
 *
 * @param code - Code du groupe de taxe (A-P)
 * @returns Taux de TVA (0 si groupe inconnu)
 */
export function getTaxRate(code) {
    const group = TAX_GROUPS[code];
    return group?.tvaRate ?? 0;
}
const SPECIAL_TAX_LABELS = {
    TDT: "TDT",
    TAXE_SEJOUR: "Taxe de séjour",
    PBA: "PBA",
};
/**
 * The special tax a group represents, `null` for an ordinary group.
 *
 * L (TDT), M (taxe de séjour) and N (PBA) are flat amounts per unit sold —
 * per person-night, per night, per ticket (CGI art. 339) — so the group
 * alone computes nothing: the article's FIXED_PER_UNIT forfait is the levy,
 * and the group's `tvaRate` applies on HT + forfait (DGI §6.9).
 */
export function specialTaxRequirement(code) {
    const group = TAX_GROUPS[code];
    const type = group?.specialTaxType;
    return type === undefined ? null : { type, label: SPECIAL_TAX_LABELS[type] };
}
//# sourceMappingURL=tax-groups.js.map